import { ADMIN_COMMON_PREFIX, ADMIN_COMPANY_PREFIX } from './adminApi';
import { getAdminToken } from './auth';

export type AdminMiniProgramContext = {
  id: string;
  key?: string;
  name?: string;
  appId?: string;
  companyId?: string;
  companyName?: string;
  companyKey?: string;
  companyIsPlatformOwner?: boolean;
  deviceLimit?: number;
};

const ADMIN_MINI_PROGRAM_CONTEXT_KEY = 'admin_mini_program_context';

type ApiResponse<T> = {
  status: number;
  result_body?: T;
};

type BackendCurrentUser = {
  company_id?: string | number;
};

type BackendMiniProgram = {
  id?: string | number;
  company_id?: string | number;
  company_key?: string;
  company_name?: string;
  company_is_platform_owner?: boolean;
  type?: string;
  key?: string;
  name?: string;
  app_id?: string;
  enabled?: boolean;
  device_limit?: number;
};

type FetchResponse = {
  ok: boolean;
  json: () => Promise<any>;
};

type Fetcher = (input: string, init?: RequestInit) => Promise<FetchResponse>;

type EnsureAdminMiniProgramContextOptions = {
  fetcher?: Fetcher;
};

let pendingEnsureContext:
  | Promise<AdminMiniProgramContext | undefined>
  | undefined;

export const getAdminMiniProgramContext = ():
  | AdminMiniProgramContext
  | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.localStorage.getItem(ADMIN_MINI_PROGRAM_CONTEXT_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as AdminMiniProgramContext;
    return parsed?.id ? parsed : undefined;
  } catch (_error) {
    window.localStorage.removeItem(ADMIN_MINI_PROGRAM_CONTEXT_KEY);
    return undefined;
  }
};

export const setAdminMiniProgramContext = (
  context?: AdminMiniProgramContext,
) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!context?.id) {
    window.localStorage.removeItem(ADMIN_MINI_PROGRAM_CONTEXT_KEY);
    return;
  }
  window.localStorage.setItem(
    ADMIN_MINI_PROGRAM_CONTEXT_KEY,
    JSON.stringify(context),
  );
};

const buildAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  token,
});

const requestJson = async <T>(
  url: string,
  token: string,
  fetcher: Fetcher,
): Promise<ApiResponse<T> | undefined> => {
  const response = await fetcher(url, {
    method: 'GET',
    headers: buildAuthHeaders(token),
  });

  if (!response.ok) {
    return undefined;
  }

  return response.json();
};

const normalizeContext = (
  miniProgram: BackendMiniProgram | undefined,
): AdminMiniProgramContext | undefined => {
  if (!miniProgram?.id) {
    return undefined;
  }

  return {
    id: String(miniProgram.id),
    key: miniProgram.key,
    name: miniProgram.name,
    appId: miniProgram.app_id,
    companyId:
      miniProgram.company_id === undefined
        ? undefined
        : String(miniProgram.company_id),
    companyName: miniProgram.company_name,
    companyKey: miniProgram.company_key,
    companyIsPlatformOwner: Boolean(miniProgram.company_is_platform_owner),
    deviceLimit: miniProgram.device_limit ?? -1,
  };
};

const loadFallbackContext = async (
  token: string,
  fetcher: Fetcher,
): Promise<AdminMiniProgramContext | undefined> => {
  const currentUserResponse = await requestJson<BackendCurrentUser>(
    `${ADMIN_COMMON_PREFIX}/current-user`,
    token,
    fetcher,
  );

  if (
    currentUserResponse?.status !== 1 ||
    !currentUserResponse.result_body?.company_id
  ) {
    return undefined;
  }

  const miniProgramsResponse = await requestJson<{
    list?: BackendMiniProgram[];
  }>(
    `${ADMIN_COMPANY_PREFIX}/mini-programs?page=1&limit=100&type=app&enabled=true`,
    token,
    fetcher,
  );

  if (miniProgramsResponse?.status !== 1) {
    return undefined;
  }

  const first = (miniProgramsResponse.result_body?.list || []).find(
    (item) =>
      (!item.type || item.type === 'app') && item.enabled !== false,
  );
  const context = normalizeContext(first);
  if (context) {
    setAdminMiniProgramContext(context);
  }

  return context;
};

export const ensureAdminMiniProgramContext = async (
  options: EnsureAdminMiniProgramContextOptions = {},
): Promise<AdminMiniProgramContext | undefined> => {
  const current = getAdminMiniProgramContext();
  if (current?.id) {
    return current;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  const token = getAdminToken();
  const fetcher = options.fetcher || window.fetch?.bind(window);
  if (!token || !fetcher) {
    return undefined;
  }

  if (!pendingEnsureContext) {
    pendingEnsureContext = loadFallbackContext(token, fetcher)
      .catch(() => undefined)
      .finally(() => {
        pendingEnsureContext = undefined;
      });
  }

  return pendingEnsureContext;
};

export { ADMIN_MINI_PROGRAM_CONTEXT_KEY };
